import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { SITE_URL, SITE_NAME } from '@/constants/seo';

const guideJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Shopping Guides — PandabuySpreadsheet',
  description: 'Explore our complete collection of SEO-optimized shopping guides covering shoes, streetwear, hoodies, bags, sneakers, accessories, and seasonal fashion finds.',
  url: `${SITE_URL}/shopping-guide`,
  hasPart: articles.map((a) => ({
    '@type': 'Article',
    headline: a.title,
    url: `${SITE_URL}/article/${a.slug}`,
    description: a.excerpt,
  })),
  isPartOf: {
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  },
};

export default function ShoppingGuide() {
  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(guideJsonLd) }}
      />
      <Navbar />

      <section className="pt-28 pb-16 md:pb-24">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <header className="mb-14">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer mb-6"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-foreground-950 leading-tight">
              Shopping Guides
            </h1>
            <p className="mt-4 text-lg text-foreground-500 max-w-2xl">
              Explore our complete collection of SEO-optimized shopping guides. Each guide covers a specific category, trend, or strategy to help you get the most out of your pandabuyspreadsheet experience.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group block bg-white rounded-2xl border border-background-200/60 overflow-hidden hover:border-background-300 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden bg-background-100">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold mb-3">
                    {article.category}
                  </span>
                  <h2 className="font-heading text-lg font-bold text-foreground-900 leading-snug group-hover:text-foreground-700 transition-colors">
                    {article.title}
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-foreground-400">
                    <span>{article.readTime} read</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span>Updated June 2026</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}